import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { callApi } from '../api';
import { Section, Stat, ErrorBox, LoadingSpinner, EmptyState } from '../components/Section';
import { useSession } from '../hooks/useSession';
import { formatDateTime, timeAgo } from '../utils';

const TABS = ['overview', 'activity', 'reviews', 'playlists', 'searches'];

export function UserDetail() {
  const { id } = useParams();
  const { session } = useSession();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('overview');
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    callApi(`/api/v1/admin/users/${id}`, { accessToken: session.access_token })
      .then(setDetail)
      .catch((e) => setError(e.message));
  }, [id, session.access_token]);

  if (error) return <ErrorBox message={error} />;
  if (!detail) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/admin/users" className="text-sm text-stone-500 underline">
          ← All users
        </Link>
        <h1 className="text-2xl font-semibold text-stone-800 mt-1">
          {detail.display_name || detail.user_id.slice(0, 8)}
          {detail.is_admin && (
            <span className="ml-2 text-xs bg-stone-800 text-white rounded px-1.5 py-0.5">
              admin
            </span>
          )}
        </h1>
        <p className="text-sm text-stone-500">
          {detail.phone || '—'} · joined {timeAgo(detail.created_at)} · last active {detail.last_active_at ? timeAgo(detail.last_active_at) : 'never'}
        </p>
      </div>

      <Section title="Profile">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat value={detail.review_count} label="reviews" />
          <Stat value={detail.playlist_count} label="playlists" />
          <Stat value={detail.follower_count} label="followers" />
          <Stat value={detail.following_count} label="following" />
          <Stat value={detail.invites_sent} label="invites sent" />
          <Stat value={detail.invites_accepted} label="invites accepted" />
          <Stat value={detail.invite_credits} label="credits left" />
          <Stat
            value={detail.taste_profile_confidence ? detail.taste_profile_confidence.toFixed(2) : '—'}
            label="taste confidence"
          />
        </div>
      </Section>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm capitalize ${
              tab === t
                ? 'border-b-2 border-stone-800 text-stone-800 font-medium'
                : 'text-stone-500'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'overview' && <CreditsAdjuster userId={id} initialCredits={detail.invite_credits} />}
      {tab === 'activity' && <ActivityTab userId={id} />}
      {tab === 'reviews' && <ReviewsTab userId={id} />}
      {tab === 'playlists' && <PlaylistsTab userId={id} />}
      {tab === 'searches' && <SearchesTab userId={id} />}
    </div>
  );
}

function CreditsAdjuster({ userId, initialCredits }) {
  const { session } = useSession();
  const [credits, setCredits] = useState(initialCredits ?? 0);
  const [delta, setDelta] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    const n = parseInt(delta, 10);
    if (Number.isNaN(n)) return;
    setSaving(true);
    setError('');
    try {
      const r = await callApi(`/api/v1/admin/users/${userId}/invite-credits`, {
        method: 'POST',
        body: { delta: n },
        accessToken: session.access_token,
      });
      setCredits(r.credits);
      setDelta('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Adjust invite credits">
      <div className="flex gap-2 items-center">
        <span className="text-sm text-stone-700">Current: {credits}</span>
        <input
          type="number"
          value={delta}
          onChange={(e) => setDelta(e.target.value)}
          placeholder="±N"
          className="border rounded px-2 py-1 w-24 text-sm"
        />
        <button
          disabled={saving || delta === ''}
          onClick={submit}
          className="bg-stone-800 text-white text-sm rounded px-3 py-1 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Apply'}
        </button>
      </div>
      <ErrorBox message={error} />
    </Section>
  );
}

function ActivityTab({ userId }) {
  const { session } = useSession();
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [type, setType] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setItems([]);
    setCursor(null);
    const q = type ? `&type=${encodeURIComponent(type)}` : '';
    callApi(`/api/v1/admin/users/${userId}/activity?limit=50${q}`, {
      accessToken: session.access_token,
    })
      .then((d) => {
        setItems(d.items);
        setHasMore(d.has_more);
        setCursor(d.next_cursor);
      })
      .catch((e) => setError(e.message));
  }, [userId, type, session.access_token]);

  return (
    <Section
      title="Recent activity"
      action={
        <input
          value={type}
          onChange={(e) => setType(e.target.value)}
          placeholder="filter by type (e.g. fyp_card_view)"
          className="text-xs border rounded px-2 py-1 w-56"
        />
      }
    >
      <ErrorBox message={error} />
      <div className="divide-y">
        {items.length === 0 ? <EmptyState>No activity</EmptyState> : items.map((it, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 text-sm">
            <span className="text-stone-700">
              <span className="font-mono text-xs bg-stone-100 rounded px-1">
                {it.interaction_type}
              </span>
              {it.restaurant_name && <span className="ml-2">{it.restaurant_name}</span>}
            </span>
            <span className="text-xs text-stone-500">{timeAgo(it.created_at)}</span>
          </div>
        ))}
      </div>
      {hasMore && (
        <button
          onClick={async () => {
            try {
              const q = type ? `&type=${encodeURIComponent(type)}` : '';
              const d = await callApi(
                `/api/v1/admin/users/${userId}/activity?limit=50&cursor=${encodeURIComponent(cursor || '')}${q}`,
                { accessToken: session.access_token }
              );
              setItems((p) => [...p, ...d.items]);
              setHasMore(d.has_more);
              setCursor(d.next_cursor);
            } catch (e) {
              setError(e.message);
            }
          }}
          className="text-sm text-stone-700 underline mt-2"
        >
          Load more
        </button>
      )}
    </Section>
  );
}

function ReviewsTab({ userId }) {
  const { session } = useSession();
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    callApi(`/api/v1/admin/users/${userId}/reviews?limit=20`, {
      accessToken: session.access_token,
    })
      .then((d) => setItems(d.items))
      .catch((e) => setError(e.message));
  }, [userId, session.access_token]);

  return (
    <Section title="Reviews">
      <ErrorBox message={error} />
      {items.length === 0 ? <EmptyState>No reviews yet</EmptyState> : (
        <div className="divide-y">
          {items.map((r) => (
            <div key={r.id} className="py-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium">{r.restaurant_name || r.restaurant_id.slice(0, 8)}</span>
                <span className="text-stone-500">
                  {r.overall_rating ? `${r.overall_rating}/5` : '—'} · {timeAgo(r.created_at)}
                </span>
              </div>
              {r.text_content && (
                <p className="text-sm text-stone-600 italic mt-0.5 line-clamp-2">
                  {r.text_content}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function PlaylistsTab({ userId }) {
  const { session } = useSession();
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    callApi(`/api/v1/admin/users/${userId}/playlists?limit=20`, {
      accessToken: session.access_token,
    })
      .then((d) => setItems(d.items))
      .catch((e) => setError(e.message));
  }, [userId, session.access_token]);

  return (
    <Section title="Playlists">
      <ErrorBox message={error} />
      {items.length === 0 ? <EmptyState>No playlists</EmptyState> : (
        <div className="divide-y">
          {items.map((p) => (
            <div key={p.id} className="flex justify-between py-2 text-sm">
              <span>
                <span className="font-medium">{p.name}</span>
                <span className="text-xs text-stone-500 ml-2">({p.role})</span>
              </span>
              <span className="text-stone-500">{p.restaurant_count} places</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function SearchesTab({ userId }) {
  const { session } = useSession();
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    callApi(`/api/v1/admin/users/${userId}/searches?limit=50`, {
      accessToken: session.access_token,
    })
      .then((d) => {
        setItems(d.items);
        setHasMore(d.has_more);
        setCursor(d.next_cursor);
      })
      .catch((e) => setError(e.message));
  }, [userId, session.access_token]);

  return (
    <Section title="Searches">
      <ErrorBox message={error} />
      {items.length === 0 ? <EmptyState>No searches</EmptyState> : (
        <div className="divide-y">
          {items.map((s, i) => (
            <div key={i} className="flex justify-between py-1.5 text-sm">
              <span className="font-mono text-xs">
                {s.raw_query || <span className="text-stone-400">— (filter only)</span>}
              </span>
              <span className="text-xs text-stone-500">
                {s.result_count ?? '—'} results · {formatDateTime(s.created_at)}
              </span>
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <button
          onClick={async () => {
            try {
              const d = await callApi(
                `/api/v1/admin/users/${userId}/searches?limit=50&cursor=${encodeURIComponent(cursor || '')}`,
                { accessToken: session.access_token }
              );
              setItems((p) => [...p, ...d.items]);
              setHasMore(d.has_more);
              setCursor(d.next_cursor);
            } catch (e) {
              setError(e.message);
            }
          }}
          className="text-sm text-stone-700 underline mt-2"
        >
          Load more
        </button>
      )}
    </Section>
  );
}
